/**
 * Service de personnalisation par tenant
 * 
 * Ce service permet à chaque tenant de personnaliser l'interface utilisateur
 * avec ses propres couleurs, logo et domaine, offrant une expérience
 * utilisateur adaptée à l'identité visuelle de chaque organisation.
 */

import tenantManager from './tenant-manager';
import tenantAPI from './tenant-api';
import tenantCacheService from './tenant-cache';

// Interface pour les paramètres de personnalisation
export interface TenantBranding {
  primaryColor: string;
  secondaryColor: string;
  accentColor?: string;
  textColor?: string;
  backgroundColor?: string;
  logoUrl?: string;
  faviconUrl?: string;
  customCss?: string;
  customFonts?: {
    primary?: string;
    secondary?: string;
  };
  darkMode?: {
    enabled: boolean;
    primaryColor?: string;
    secondaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
  };
}

// Valeurs par défaut
const DEFAULT_BRANDING: TenantBranding = {
  primaryColor: '#3b82f6', // blue-500
  secondaryColor: '#6366f1', // indigo-500
  accentColor: '#10b981', // emerald-500
  textColor: '#1f2937', // gray-800
  backgroundColor: '#ffffff',
  darkMode: {
    enabled: true,
    primaryColor: '#3b82f6', // blue-500
    secondaryColor: '#6366f1', // indigo-500
    backgroundColor: '#111827', // gray-900
    textColor: '#f9fafb', // gray-50
  }
};

export class TenantBrandingService {
  /**
   * Applique la personnalisation pour un tenant spécifique
   */
  async applyBranding(tenantId: string): Promise<void> {
    // Vérifier l'accès au tenant
    if (!tenantManager.isTenantAccessible(tenantId)) {
      console.warn(`Tentative d'application du branding pour un tenant inaccessible: ${tenantId}`);
      return;
    }
    
    try {
      // Récupérer les paramètres de personnalisation, avec mise en cache
      const branding = await tenantCacheService.getOrSet<TenantBranding>(
        tenantId,
        'branding',
        async () => {
          const tenant = await tenantAPI.getTenant(tenantId);
          return tenant?.settings?.branding || DEFAULT_BRANDING;
        },
        3600 // Cache d'une heure
      );
      
      // Appliquer les couleurs et styles CSS
      this.applyColors(branding);
      
      // Appliquer le logo et favicon
      this.applyLogo(branding);
      
      // Appliquer les polices personnalisées
      this.applyFonts(branding);
      
      // Appliquer le CSS personnalisé
      this.applyCustomCss(branding);
      
      console.log(`[Branding] Personnalisation appliquée pour le tenant ${tenantId}`);
    } catch (error) {
      console.error(`Erreur lors de l'application du branding pour le tenant ${tenantId}:`, error);
    }
  }
  
  /**
   * Réinitialise la personnalisation aux valeurs par défaut
   */
  resetBranding(): void {
    this.applyColors(DEFAULT_BRANDING);
    
    // Réinitialiser le logo et favicon
    const favicon = document.querySelector('link[rel="icon"]');
    if (favicon && favicon instanceof HTMLLinkElement) {
      favicon.href = '/favicon.ico';
    }
    
    // Supprimer les polices personnalisées
    const fontStyleElement = document.getElementById('tenant-custom-fonts');
    if (fontStyleElement) {
      fontStyleElement.remove();
    }
    
    // Supprimer le CSS personnalisé
    const customStyleElement = document.getElementById('tenant-custom-css');
    if (customStyleElement) {
      customStyleElement.remove();
    }
    
    console.log('[Branding] Personnalisation réinitialisée aux valeurs par défaut');
  }
  
  /**
   * Applique les couleurs personnalisées
   */
  private applyColors(branding: TenantBranding): void {
    const isDarkMode = this.isDarkMode();
    const root = document.documentElement;
    
    // Appliquer les couleurs en fonction du mode (clair/sombre)
    if (isDarkMode && branding.darkMode?.enabled) {
      // Mode sombre
      root.style.setProperty('--color-primary', branding.darkMode.primaryColor || branding.primaryColor);
      root.style.setProperty('--color-secondary', branding.darkMode.secondaryColor || branding.secondaryColor);
      root.style.setProperty('--color-background', branding.darkMode.backgroundColor || '#111827');
      root.style.setProperty('--color-text', branding.darkMode.textColor || '#f9fafb');
    } else {
      // Mode clair
      root.style.setProperty('--color-primary', branding.primaryColor);
      root.style.setProperty('--color-secondary', branding.secondaryColor);
      root.style.setProperty('--color-background', branding.backgroundColor || '#ffffff');
      root.style.setProperty('--color-text', branding.textColor || '#1f2937');
    }
    
    // Couleur d'accent (commune aux deux modes)
    if (branding.accentColor) {
      root.style.setProperty('--color-accent', branding.accentColor);
    }
  }
  
  /**
   * Applique le logo et favicon personnalisés
   */
  private applyLogo(branding: TenantBranding): void {
    // Changer le favicon
    if (branding.faviconUrl) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon && favicon instanceof HTMLLinkElement) {
        favicon.href = branding.faviconUrl;
      }
    }
    
    // Changer le logo dans l'interface
    if (branding.logoUrl) {
      // Trouver tous les éléments avec la classe 'app-logo'
      const logoElements = document.querySelectorAll('.app-logo');
      logoElements.forEach(element => {
        if (element instanceof HTMLImageElement) {
          element.src = branding.logoUrl!;
          element.alt = tenantManager.getAccessibleTenants().find(t => t.id === tenantManager.getCurrentTenantId())?.name || 'Logo';
        }
      });
    }
  }
  
  /**
   * Applique les polices personnalisées
   */
  private applyFonts(branding: TenantBranding): void {
    if (!branding.customFonts) return;
    
    // Supprimer les styles de police précédents
    let fontStyleElement = document.getElementById('tenant-custom-fonts');
    if (!fontStyleElement) {
      fontStyleElement = document.createElement('style');
      fontStyleElement.id = 'tenant-custom-fonts';
      document.head.appendChild(fontStyleElement);
    }
    
    // Construire le CSS pour les polices personnalisées
    let fontCss = '';
    
    if (branding.customFonts.primary) {
      fontCss += `
        body, .font-primary {
          font-family: "${branding.customFonts.primary}", system-ui, sans-serif;
        }
      `;
    }
    
    if (branding.customFonts.secondary) {
      fontCss += `
        h1, h2, h3, h4, h5, h6, .font-secondary {
          font-family: "${branding.customFonts.secondary}", system-ui, sans-serif;
        }
      `;
    }
    
    fontStyleElement.textContent = fontCss;
  }
  
  /**
   * Applique le CSS personnalisé
   */
  private applyCustomCss(branding: TenantBranding): void {
    if (!branding.customCss) return;
    
    // Supprimer le CSS personnalisé précédent
    let customStyleElement = document.getElementById('tenant-custom-css');
    if (!customStyleElement) {
      customStyleElement = document.createElement('style');
      customStyleElement.id = 'tenant-custom-css';
      document.head.appendChild(customStyleElement);
    }
    
    // Appliquer le CSS personnalisé
    customStyleElement.textContent = branding.customCss;
  }
  
  /**
   * Détecte si le mode sombre est activé
   */
  private isDarkMode(): boolean {
    // Vérifier la préférence de l'utilisateur
    const userPreference = localStorage.getItem('darkMode');
    if (userPreference !== null) {
      return userPreference === 'true';
    }
    
    // Sinon, utiliser la préférence du système
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
  
  /**
   * Met à jour le titre de la page avec le nom du tenant
   */
  updatePageTitle(tenantName?: string): void {
    const tenant = tenantName || tenantManager.getAccessibleTenants().find(t => t.id === tenantManager.getCurrentTenantId())?.name;
    if (tenant) {
      document.title = `${tenant} - Sentinel`;
    } else {
      document.title = 'Sentinel - Sécurité & Monitoring';
    }
  }
}

// Exporter une instance unique du service
export const tenantBrandingService = new TenantBrandingService();

export default tenantBrandingService;
